import {formatDate, formatNumber} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {AdminDashboardReportBlock, AdminDashboardReportProvider, AdminDashboardReportRow} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {adbCategoryAxis, adbChartTheme, adbGrid, adbLegend, adbRedrawOnThemeChange, adbTooltip, adbValueAxis} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {
  AdminDashboardInitFailures,
  AdminDashboardInitStepFailure,
  AdminDashboardQueryParams,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';

/** The ECharts entry point behind a plain object, so a spec can stub it (`spyOn(echarts, 'init')` throws). */
export const initFailuresCharts = {init: (element: HTMLElement): echarts.ECharts => echarts.init(element)};

/**
 * Product quality: failed sign-ins at each login-initialisation step, this period against the previous one. A step more
 * than 1.5x its previous count is drawn red. When the cluster records no startup steps the card says so instead of
 * drawing an empty chart. Reloads whenever the dashboard's params change.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-init-failures',
  templateUrl: './admin-dashboard-init-failures.component.html',
  styleUrls: ['./admin-dashboard-init-failures.component.scss']
})
export class AdminDashboardInitFailuresComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  @Input() params: AdminDashboardQueryParams;
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  collected = false;
  steps: AdminDashboardInitStepFailure[] = [];
  loaded = false;
  /** The last load failed: the card says so instead of saying the steps are not collected. */
  failed = false;
  readonly failedTitle = 'Couldn\'t load login-initialisation failures by step';

  private chartElement: HTMLDivElement;
  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry,
    private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('product-quality', this.host.nativeElement, this);
  }

  @ViewChild('chart') set chartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.chartElement) {
      return;
    }
    this.disposeChart();
    if (container) {
      this.chartElement = container.nativeElement;
      this.chart = initFailuresCharts.init(this.chartElement);
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
      this.resizeObserver.observe(this.chartElement);
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.disposeChart();
    this.reportRegistry.unregister(this);
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-11). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    const title = 'Login-initialisation failures by step';
    if (this.failed) {
      return [{kind: 'empty', title, text: this.failedTitle}];
    }
    if (!this.collected) {
      return [{
        kind: 'empty',
        title,
        text: 'Not collected in this cluster yet — step records are written once in-process startup (UseInProcessStartup) is on.'
      }];
    }
    if (!this.steps.length) {
      return [{kind: 'empty', title, text: 'No failed sign-in steps in this view'}];
    }
    const worst = this.worst;
    const subtitle = worst
      ? `${worst.step} failures are up ${this.worstChange} — worst on ${formatDate(worst.worstDay, 'd MMM', 'en-US', 'UTC')} with ${worst.worstValue}. `
        + 'Red marks a step more than 1.5× the previous period.'
      : 'Failed sign-ins at each step of initialisation, against the previous period.';
    return [
      {
        kind: 'chart',
        title,
        subtitle,
        image: this.chart ? this.chart.getDataURL({type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF'}) : null,
        widthPx: this.chart?.getWidth() ?? 0,
        heightPx: this.chart?.getHeight() ?? 0
      },
      {
        // The numbers behind the bars; a step drawn red on screen (more than 1.5x its previous count) gets the red stripe.
        kind: 'table',
        title: 'Failed sign-ins by step',
        columns: [{header: 'Step', weight: 3}, {header: 'This period', align: 'right'}, {header: 'Previous period', align: 'right'}],
        rows: this.steps.map((step): AdminDashboardReportRow => {
          const cells = [step.step, String(step.current), String(step.previous)];
          return step.previous && step.current / step.previous > 1.5 ? {cells, tone: 'red'} : {cells};
        })
      }
    ];
  }

  /** The step that grew most against a non-zero previous count, when it is more than 1.3x; else null. */
  get worst(): AdminDashboardInitStepFailure | null {
    const rising = this.steps
      .filter(step => step.previous > 0 && !!step.worstDay)
      .sort((a, b) => b.current / b.previous - a.current / a.previous)[0];
    return rising && rising.current / rising.previous > 1.3 ? rising : null;
  }

  /** "160.0%" */
  get worstChange(): string {
    const worst = this.worst;
    return worst ? `${(((worst.current - worst.previous) / worst.previous) * 100).toFixed(1)}%` : '';
  }

  /** "26 failed sign-ins · 10 in the previous period" */
  tooltipText(step: AdminDashboardInitStepFailure): string {
    return `${step?.current ?? 0} failed sign-ins · ${step?.previous ?? 0} in the previous period`;
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getLoginInitFailures(this.params).subscribe({
      next: result => this.show(result, false),
      error: () => this.show(null, true)
    });
  }

  /** Not collected in this cluster yet, and collected with no failed step, both have nothing to show: they report empty. */
  private show(result: AdminDashboardInitFailures | null, failed: boolean): void {
    this.collected = result?.Collected === true;
    this.steps = result?.Steps ?? [];
    this.failed = failed;
    this.loaded = true;
    this.render();
    this.report(failed ? 'failed' : this.collected && this.steps.length ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }

  private disposeChart(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chart?.dispose();
    this.chart = null;
    this.chartElement = null;
  }

  /**
   * The OCA-E-159 init chart (M/charts.ts initOption). "This period" carries its colour on the series, so the legend
   * swatch matches the bars; a step more than 1.5x its previous count is drawn red per item.
   */
  private render(): void {
    if (!this.chart) {
      return;
    }
    const steps = this.steps;
    const t = adbChartTheme(this.host.nativeElement);
    const failing = (step: AdminDashboardInitStepFailure): boolean => !!step.previous && step.current / step.previous > 1.5;
    const option: echarts.EChartsOption = {
      grid: adbGrid({top: 30}),
      legend: {...adbLegend(t), data: ['This period', 'Previous period']},
      xAxis: adbCategoryAxis(t, steps.map(s => s.step)),
      yAxis: adbValueAxis(t, (value: number) => formatNumber(value, 'en-US', '1.0-0'), {min: 0}),
      series: [
        {
          name: 'Previous period',
          type: 'bar',
          data: steps.map(s => s.previous),
          barMaxWidth: 18,
          itemStyle: {color: t.line, borderRadius: [3, 3, 0, 0]}
        },
        {
          name: 'This period',
          type: 'bar',
          barMaxWidth: 18,
          itemStyle: {color: t.blue2, borderRadius: [3, 3, 0, 0]},
          data: steps.map(s => ({value: s.current, itemStyle: {color: failing(s) ? t.red : t.blue2}}))
        }
      ],
      tooltip: adbTooltip(t, {
        axisPointer: {type: 'shadow'},
        formatter: (params: any) => {
          const point = Array.isArray(params) ? params[0] : params;
          return `<b>${point?.name}</b><br/>${this.tooltipText(steps[point?.dataIndex])}`;
        }
      })
    };
    this.chart.setOption(option, true);
  }
}
